import { useEffect, useMemo, useState } from "react";

const API_BASE = "https://elshowdelasbanqueras.com/show-banqueras-api";

const ENDPOINTS = {
  create: `${API_BASE}/api/episodes_create.php`,
  list: `${API_BASE}/api/episodes_list.php`,
};

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&w=1200&q=60";

function isValidUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function EpisodesManagerServer() {
  const [episodes, setEpisodes] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", link: "" });
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState("");

  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const canSubmit = useMemo(() => {
    return (
      form.title.trim().length >= 3 &&
      form.description.trim().length >= 10 &&
      isValidUrl(form.link.trim())
    );
  }, [form]);

  const titleLen = form.title.trim().length;
  const descLen = form.description.trim().length;
  const linkOk = form.link.trim() ? isValidUrl(form.link.trim()) : false;

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onPickThumb = (e) => {
    const file = e.target.files?.[0] || null;
    setThumbFile(file);
    if (thumbPreview) URL.revokeObjectURL(thumbPreview);
    if (file) setThumbPreview(URL.createObjectURL(file));
    else setThumbPreview("");
  };

  const fetchEpisodes = async () => {
    setListLoading(true);
    try {
      const res = await fetch(ENDPOINTS.list);
      const data = await res.json();
      setEpisodes(Array.isArray(data) ? data : []);
    } catch {
      setEpisodes([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchEpisodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);

    if (!canSubmit) {
      setErr("Completa todos los campos correctamente.");
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("description", form.description.trim());
      fd.append("link", form.link.trim());
      if (thumbFile) fd.append("thumbnail", thumbFile);

      const res = await fetch(ENDPOINTS.create, {
        method: "POST",
        body: fd,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Error");

      setOk(true);
      setForm({ title: "", description: "", link: "" });
      setThumbFile(null);
      if (thumbPreview) URL.revokeObjectURL(thumbPreview);
      setThumbPreview("");
      await fetchEpisodes();

      setTimeout(() => setOk(false), 2200);
    } catch (e2) {
      setErr(e2.message || "Error guardando episodio");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="container">
      <div className="grid grid-2" style={{ gap: 16, alignItems: "start" }}>
        {/* ===== FORM ===== */}
        <form
          className="card"
          onSubmit={onSubmit}
          style={{
            maxWidth: 920,
            marginInline: "auto",
            borderRadius: 18,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div>
              <h2 style={{ marginTop: 0, marginBottom: 6 }}>🎙️ Crear episodio</h2>
              <p className="p" style={{ marginTop: 0, opacity: 0.85 }}>
                Sube una miniatura (opcional), escribe una descripción y pega el link del episodio.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-ghost"
              onClick={fetchEpisodes}
              disabled={listLoading}
              title="Actualizar listado"
            >
              {listLoading ? "Actualizando..." : "Actualizar"}
            </button>
          </div>

          {/* Alerts */}
          {err && (
            <div
              className="card"
              style={{
                marginTop: 12,
                borderRadius: 16,
                border: "1px solid rgba(255, 123, 123, .45)",
                background: "rgba(255, 123, 123, .08)",
              }}
            >
              ❌ {err}
            </div>
          )}

          {ok && (
            <div
              className="card"
              style={{
                marginTop: 12,
                borderRadius: 16,
                border: "1px solid rgba(76, 175, 80, .45)",
                background: "rgba(76, 175, 80, .10)",
              }}
            >
              ✅ Episodio guardado
            </div>
          )}

          <div style={{ height: 14 }} />

          {/* Miniatura */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div
              className="card"
              style={{
                width: 180,
                height: 110,
                borderRadius: 16,
                padding: 0,
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,.06)",
                border: "2px solid rgba(59, 130, 246, .35)",
              }}
            >
              {thumbPreview ? (
                <img
                  src={thumbPreview}
                  alt="Miniatura"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{ textAlign: "center", opacity: 0.75, fontSize: 12, padding: 10 }}>
                  <div style={{ fontWeight: 900 }}>Miniatura</div>
                  <div style={{ marginTop: 4 }}>Opcional</div>
                </div>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 240 }}>
              <label style={{ display: "block", fontWeight: 900, marginBottom: 6 }}>
                Imagen miniatura (opcional)
              </label>
              <input type="file" accept="image/*" onChange={onPickThumb} />
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                Recomendado: JPG/PNG/WebP • 1200px ancho (aprox.)
              </div>

              {thumbPreview && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    setThumbFile(null);
                    if (thumbPreview) URL.revokeObjectURL(thumbPreview);
                    setThumbPreview("");
                  }}
                >
                  Quitar miniatura
                </button>
              )}
            </div>
          </div>

          <div style={{ height: 14 }} />

          {/* Campos */}
          <label style={{ fontWeight: 900, display: "block", marginBottom: 6 }}>
            Título <span style={{ opacity: 0.7, fontWeight: 700 }}>({titleLen}/3+)</span>
          </label>
          <input
            className="card"
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 14,
              border: titleLen >= 3 ? "1px solid rgba(76,175,80,.35)" : "1px solid rgba(255,255,255,.12)",
            }}
            placeholder="Ej: EP6 – Tema del día"
            name="title"
            value={form.title}
            onChange={onChange}
          />

          <div style={{ height: 12 }} />

          <label style={{ fontWeight: 900, display: "block", marginBottom: 6 }}>
            Descripción <span style={{ opacity: 0.7, fontWeight: 700 }}>({descLen}/10+)</span>
          </label>
          <textarea
            className="card"
            style={{
              width: "100%",
              padding: 12,
              minHeight: 130,
              borderRadius: 14,
              border: descLen >= 10 ? "1px solid rgba(76,175,80,.35)" : "1px solid rgba(255,255,255,.12)",
            }}
            placeholder="Cuenta brevemente de qué trata el episodio..."
            name="description"
            value={form.description}
            onChange={onChange}
          />

          <div style={{ height: 12 }} />

          <label style={{ fontWeight: 900, display: "block", marginBottom: 6 }}>
            Link del episodio{" "}
            <span style={{ opacity: 0.7, fontWeight: 700 }}>
              {form.link.trim() ? (linkOk ? "✅ válido" : "⚠️ inválido") : ""}
            </span>
          </label>
          <input
            className="card"
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 14,
              border: form.link.trim()
                ? linkOk
                  ? "1px solid rgba(76,175,80,.35)"
                  : "1px solid rgba(255, 184, 107, .45)"
                : "1px solid rgba(255,255,255,.12)",
            }}
            placeholder="https://www.youtube.com/watch?v=..."
            name="link"
            value={form.link}
            onChange={onChange}
          />

          <div style={{ height: 14 }} />

          <button
            className="btn btn-blue"
            type="submit"
            disabled={loading || !canSubmit}
            style={{
              width: "100%",
              justifyContent: "center",
              opacity: loading || !canSubmit ? 0.7 : 1,
            }}
          >
            {loading ? "Guardando..." : "Guardar episodio"}
          </button>

          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 10, lineHeight: 1.35 }}>
            * El episodio se guardará en el servidor y aparecerá en la web cuando se liste desde la API.
          </div>
        </form>

        {/* ===== LISTADO ===== */}
        <div className="card" style={{ borderRadius: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <h2 style={{ margin: 0 }}>📌 Episodios publicados</h2>
            <button className="btn btn-ghost" onClick={fetchEpisodes} disabled={listLoading}>
              {listLoading ? "..." : "Actualizar"}
            </button>
          </div>

          <div style={{ height: 12 }} />

          {listLoading ? (
            <div style={{ opacity: 0.75 }}>Cargando episodios...</div>
          ) : episodes.length === 0 ? (
            <div className="card" style={{ opacity: 0.85, borderRadius: 16 }}>
              No hay episodios aún.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {episodes.map((ep) => {
                const raw = ep?.thumbnail_url || ep?.image || "";
                const img = String(raw).trim()
                  ? String(raw).startsWith("http")
                    ? raw
                    : `${API_BASE}/${String(raw).replace(/^\/+/, "")}`
                  : FALLBACK_IMG;

                return (
                  <div key={ep.id ?? ep.title} className="card" style={{ padding: 12, borderRadius: 16 }}>
                    <div style={{ display: "flex", gap: 12 }}>
                      <div
                        style={{
                          width: 140,
                          height: 90,
                          borderRadius: 14,
                          overflow: "hidden",
                          background: "rgba(255,255,255,.06)",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={img}
                          alt={ep?.title || "Episodio"}
                          onError={(e) => (e.currentTarget.src = FALLBACK_IMG)}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          loading="lazy"
                        />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 900, lineHeight: 1.2 }}>
                          {ep?.title || "Episodio"}
                        </div>

                        <div style={{ opacity: 0.85, marginTop: 6, fontSize: 13, lineHeight: 1.35 }}>
                          {ep?.description || "Sin descripción."}
                        </div>

                        <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                          {ep?.link ? (
                            <a className="btn btn-ghost" href={ep.link} target="_blank" rel="noreferrer">
                              Ver episodio →
                            </a>
                          ) : (
                            <span style={{ fontSize: 12, opacity: 0.7 }}>Sin link</span>
                          )}
                        </div>

                        {ep?.created_at && (
                          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
                            {new Date(ep.created_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
